import API_URL from '../config';
import { getAuthHeaders } from '../utils/api';

/**
 * Create a new puzzle room
 * @param {Object} roomData - Room configuration object
 * @returns {Promise<Object>} Created room with invite URL
 */
export const createRoom = async (roomData) => {
  const response = await fetch(`${API_URL}/api/rooms`, {
    method: 'POST',
    headers: getAuthHeaders({ json: true }),
    body: JSON.stringify(roomData),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create room');
  }

  return response.json();
};

/**
 * Get all rooms created by authenticated user
 * @returns {Promise<Array>} List of rooms
 */
export const getMyRooms = async () => {
  const response = await fetch(`${API_URL}/api/rooms/my-rooms`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch rooms');
  }

  return response.json();
};

/**
 * Get room info by invite code
 * @param {string} code - Room invite code
 * @returns {Promise<Object>} Room data with puzzle
 */
export const getRoom = async (code) => {
  const response = await fetch(`${API_URL}/api/rooms/${code}`, {
    headers: getAuthHeaders({ includeGuestId: true }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch room');
  }

  return response.json();
};

/**
 * Get room leaderboard
 * @param {string} code - Room invite code
 * @returns {Promise<Object>} Leaderboard data
 */
export const getRoomLeaderboard = async (code) => {
  const response = await fetch(`${API_URL}/api/rooms/${code}/leaderboard`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch leaderboard');
  }

  return response.json();
};

/**
 * Join a puzzle room
 * @param {string} code - Room invite code
 * @param {string} displayName - Optional display name for guest users
 * @returns {Promise<Object>} Join result
 */
export const joinRoom = async (code, displayName = null) => {
  const response = await fetch(`${API_URL}/api/rooms/${code}/join`, {
    method: 'PATCH',
    headers: getAuthHeaders({ json: true, includeGuestId: true }),
    body: JSON.stringify({ displayName }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to join room');
  }

  return response.json();
};

/**
 * Mark puzzle as complete in room
 * @param {string} code - Room invite code
 * @param {Object} stats - { playTime, moveCount }
 * @returns {Promise<Object>} Completion result with rank
 */
export const completeRoomPuzzle = async (code, stats) => {
  const response = await fetch(`${API_URL}/api/rooms/${code}/complete`, {
    method: 'PATCH',
    headers: getAuthHeaders({ json: true, includeGuestId: true }),
    body: JSON.stringify(stats),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to record completion');
  }

  return response.json();
};

/**
 * Delete a room (creator only)
 * @param {string} code - Room invite code
 * @returns {Promise<Object>} Delete result
 */
export const deleteRoom = async (code) => {
  const response = await fetch(`${API_URL}/api/rooms/${code}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete room');
  }

  return response.json();
};

/**
 * Get current user's game state for this room
 * @param {string} code - Room invite code
 * @returns {Promise<Object>} Participant's game state
 */
export const getRoomParticipantState = async (code) => {
  const response = await fetch(`${API_URL}/api/rooms/${code}/state`, {
    headers: getAuthHeaders({ includeGuestId: true }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch state');
  }

  return response.json();
};

/**
 * Save current user's game state for this room
 * @param {string} code - Room invite code
 * @param {Object} stateData - Game state data to save
 * @returns {Promise<Object>} Save result
 */
export const saveRoomParticipantState = async (code, stateData) => {
  const response = await fetch(`${API_URL}/api/rooms/${code}/state`, {
    method: 'PATCH',
    headers: getAuthHeaders({ json: true, includeGuestId: true }),
    body: JSON.stringify(stateData),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to save state');
  }

  return response.json();
};
